<!--
  ~ Copyright (c) 2023 D2ArmorPicker by Mijago.
  ~
  ~ This program is free software: you can redistribute it and/or modify
  ~ it under the terms of the GNU Affero General Public License as published
  ~ by the Free Software Foundation, either version 3 of the License, or
  ~ (at your option) any later version.
  ~
  ~ This program is distributed in the hope that it will be useful,
  ~ but WITHOUT ANY WARRANTY; without even the implied warranty of
  ~ MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  ~ GNU Affero General Public License for more details.
  ~
  ~ You should have received a copy of the GNU Affero General Public License
  ~ along with this program.  If not, see <http://www.gnu.org/licenses/>.
  -->

<mat-card appearance="outlined" id="card-results">
  <mat-card-header>
    <mat-card-title>Results</mat-card-title>
  </mat-card-header>
  <mat-card-content>
    <div>
      <div class="info-stats-container">
        <div class="info-stat-card">
          <span class="info-stat-label">Items Used</span>
          <span class="info-stat-value">{{ this.itemCount | number }}</span>
        </div>
        <div class="info-stat-card">
          <span class="info-stat-label">Combinations generated</span>
          <div class="info-stat-value-container">
            <span class="info-stat-value">{{ this.totalResults | number }}</span>
            <mat-icon
              #tooltip="matTooltip"
              *ngIf="this.parsedResults !== this.totalResults && this.parsedResults === 5e4"
              class="report-problem-icon"
              matTooltip="Note: To speed up the whole process, only {{
                this.parsedResults | number
              }} results are listed in this table.
                          If you need more entries, disable the limitation in the advanced settings."
              >report_problem
            </mat-icon>
            <mat-icon
              #tooltip="matTooltip"
              *ngIf="this.parsedResults !== this.totalResults && this.parsedResults === 1e6"
              class="report-problem-icon"
              matTooltip="Note: To prevent Out-Of-Memory crashes, only {{
                this.parsedResults | number
              }} results are listed in this table.
                                  You should narrow down your settings a bit."
              >report_problem
            </mat-icon>
          </div>
        </div>
        <div class="info-stat-card">
          <span class="info-stat-label">Time required</span>
          <span class="info-stat-value">{{ this.totalTime | number }}ms</span>
        </div>
        <div class="view-mode-toggle">
          <mat-button-toggle-group
            [value]="viewMode"
            (change)="onViewModeChange($event)"
            aria-label="View Mode">
            <mat-button-toggle value="cards">
              <mat-icon>view_module</mat-icon>
              Cards
            </mat-button-toggle>
            <mat-button-toggle value="table">
              <mat-icon>table_view</mat-icon>
              Table
            </mat-button-toggle>
          </mat-button-toggle-group>
        </div>
      </div>

      <div class="config-summary-container">
        <h4 class="config-summary-title">Summary of important configuration choices</h4>
        <mat-chip-listbox
          aria-label="Configuration Summary"
          class="config-summary-chips"
          [selectable]="false">
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_selectedExotics.indexOf(-1) > -1"
            disableRipple
            matTooltip="This setting enforces that all exotics are ignored."
            >No Exotic
          </mat-chip-option>
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="
              _config_selectedExotics.length >= 1 && _config_selectedExotics.indexOf(-1) === -1
            "
            disableRipple
            matTooltip="This setting enforces that only one specific exotic is used."
            >Exotic
          </mat-chip-option>
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_tryLimitWastedStats"
            disableRipple
            matTooltip="The tool will add minor stat mods to reduce wasted stats."
            >Reduce wasted stats
          </mat-chip-option>
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_modslotLimitation"
            disableRipple
            matTooltip="This setting limits available stat mod types, like major Super or Class."
            >Stat Mod Limitation
          </mat-chip-option>
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_armorPerkLimitation"
            disableRipple
            matTooltip="This setting enforces an specific armor perk or modslot for a specific armor slot.">
            Armor Perk or Slot
          </mat-chip-option>
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_onlyShowResultsWithNoWastedStats"
            disableRipple
            matTooltip="This setting means that only builds with no wasted stats are shown."
            selected
            >Zero Waste
          </mat-chip-option>
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="
              (_config_assumeEveryLegendaryIsArtifice && _config_legacyArmor) ||
              _config_assumeEveryExoticIsArtifice
            "
            disableRipple
            matTooltip="Some legacy items are assumed to have an artifice slot."
            color="warn">
            <mat-icon inline style="height: 100%">report_problem</mat-icon>
            &nbsp;Legacy Artifice Assumptions&nbsp;
            <mat-icon inline style="height: 100%">report_problem</mat-icon>
          </mat-chip-option>

          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_onlyUseMasterworkedExotics"
            disableRipple
            matTooltip="This setting means that only exotic armor pieces that are already masterworked are used."
            selected>
            Masterworked Exotics Only
          </mat-chip-option>

          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_onlyUseMasterworkedLegendaries"
            disableRipple
            matTooltip="This setting means that only legendary armor pieces are already masterworked are used."
            selected>
            Masterworked Legendaries Only
          </mat-chip-option>

          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="
              !_config_onlyUseMasterworkedExotics &&
              !_config_onlyUseMasterworkedLegendaries &&
              (_config_assumeLegendariesMasterworked || _config_assumeExoticsMasterworked)
            "
            disableRipple
            matTooltip="Some masterwork assumptions are in place. This means that you may have to masterwork items. Look at your advanced settings to see which ones are activated.">
            Masterwork Assumption
          </mat-chip-option>
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_enforceFeaturedArmor"
            disableRipple
            matTooltip="This setting enforces that only featured armor pieces (from specific activities or vendors) are used in builds."
            selected>
            Featured Armor Only
          </mat-chip-option>
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_includeCollectionRolls"
            disableRipple
            matTooltip="Collection Exotic rolls will be included in the search.">
            Include Collection Rolls
          </mat-chip-option>
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_includeVendorRolls"
            disableRipple
            matTooltip="Currently available Vendor items will be included in the search.">
            Include Vendor Items
          </mat-chip-option>
        </mat-chip-listbox>
      </div>

      <div class="initializing-container" *ngIf="initializing">
        <mat-icon class="rotate">hourglass_top</mat-icon>
        <h3>Initializing...</h3>
        <p>Please wait while the application is loading your data.</p>
        <p>
          This may take a while, depending on your settings and the number of items you have.
          <br />
          Please be patient.
        </p></div
      >

      <div
        class="progress-bar-container"
        *ngIf="_results.length === 0 && isCalculatingPermutations && !initializing">
        <mat-icon class="rotate"> hourglass_top </mat-icon>
        <h3>Calculating permutations...</h3>
        <mat-progress-bar
          [value]="computationProgress"
          aria-label="Calculating permutations..."></mat-progress-bar>
        <p>
          This may take a while, depending on your settings and the number of items you have.
          <br />
          Please be patient.
        </p>

        <br />
        <button
          mat-raised-button
          color="warn"
          matTooltip="Cancel the current calculation. This is useful if you want to change your settings or if the calculation takes too long."
          (click)="cancelCalculation()"
          [disabled]="!isCalculatingPermutations">
          Cancel Calculation
        </button>
      </div>

      <!-- Cancelled Message -->
      <div class="cancelled-message" *ngIf="_results.length === 0 && cancelledCalculation">
        <mat-icon>cancel</mat-icon>
        <h3>You cancelled the calculation process</h3>
        <p>
          The calculation has been cancelled. You can try again with different settings.
          <br />
          If you want to continue, just change anything in your settings.
        </p>
      </div>

      <!-- No results message -->
      <div
        class="no-results"
        *ngIf="
          _results.length === 0 &&
          !isCalculatingPermutations &&
          !initializing &&
          !cancelledCalculation
        ">
        <mat-icon>search_off</mat-icon>
        <h3>No results found</h3>
        <p>Try adjusting your filters or search criteria.</p>
        <div id="common-issues">
          <p>Common issues:</p>
          <ul>
            <li> Desired stats may simply not be possible with your current configuration. </li>
            <li>Make sure that you have at least one legendary item per slot.</li>
            <li *ngIf="_config_enforceFeaturedArmor">
              You enforced that only featured armor is used. Do you have at least one piece of
              featured armor for each slot?
            </li>
            <li *ngIf="!_config_legacyArmor">
              Maybe you wanted to include legacy (Armor 2.0) armor too?
            </li>
            <li *ngIf="!_config_includeVendorRolls"> Maybe you wanted to include vendor items? </li>
            <li *ngIf="!_config_includeCollectionRolls">
              Maybe you wanted to include exotics in the collection?
            </li>
          </ul>
        </div>
      </div>

      <!-- Results Content -->
      <div *ngIf="_results.length > 0">
        <!-- Card View -->
        <app-results-card-view *ngIf="viewMode === 'cards'" [results]="_results">
        </app-results-card-view>

        <!-- Table View -->
        <table
          *ngIf="viewMode === 'table'"
          [dataSource]="tableDataSource"
          class="result-table"
          mat-table
          matSort
          matSortActive="Mods"
          matSortDirection="asc"
          multiTemplateDataRows>
          <!--- Note that these columns can be defined in any order.
           The actual rendered columns are set as a property on the row definition" -->
          <!-- Name Column -->
          <ng-container matColumnDef="weapon">
            <th
              *matHeaderCellDef
              mat-header-cell
              mat-sort-header="Weapon"
              matTooltip="The weapon Stat of this armor combination."
              >Weapon
            </th>
            <td *matCellDef="let element" class="statColumn" mat-cell
              >{{ element.stats[ArmorStat.StatWeapon] }}
              <app-stat-icon [stat]="ArmorStat.StatWeapon" class="statIcon"></app-stat-icon>
            </td>
          </ng-container>

          <!-- Name Column -->
          <ng-container matColumnDef="health">
            <th
              *matHeaderCellDef
              mat-header-cell
              mat-sort-header="Health"
              matTooltip="The  stat of this armor combination.">
              Health
            </th>
            <td *matCellDef="let element" class="statColumn" mat-cell
              >{{ element.stats[ArmorStat.StatHealth] }}
              <app-stat-icon [stat]="ArmorStat.StatHealth" class="statIcon"></app-stat-icon>
            </td>
          </ng-container>

          <!-- Name Column -->
          <ng-container matColumnDef="class">
            <th
              *matHeaderCellDef
              mat-header-cell
              mat-sort-header="Class"
              matTooltip="The class stat of this armor combination.">
              Class
            </th>
            <td *matCellDef="let element" class="statColumn" mat-cell
              >{{ element.stats[ArmorStat.StatClass] }}
              <app-stat-icon [stat]="ArmorStat.StatClass" class="statIcon"></app-stat-icon>
            </td>
          </ng-container>

          <!-- Name Column -->
          <ng-container matColumnDef="grenade">
            <th
              *matHeaderCellDef
              mat-header-cell
              mat-sort-header="Grenade"
              matTooltip="The grenade stat of this armor combination.">
              Grenade
            </th>
            <td *matCellDef="let element" class="statColumn" mat-cell
              >{{ element.stats[ArmorStat.StatGrenade] }}
              <app-stat-icon [stat]="ArmorStat.StatGrenade" class="statIcon"></app-stat-icon>
            </td>
          </ng-container>

          <!-- Name Column -->
          <ng-container matColumnDef="super">
            <th
              *matHeaderCellDef
              mat-header-cell
              mat-sort-header="Super"
              matTooltip="The super stat of this armor combination.">
              Super
            </th>
            <td *matCellDef="let element" class="statColumn" mat-cell
              >{{ element.stats[ArmorStat.StatSuper] }}
              <app-stat-icon [stat]="ArmorStat.StatSuper" class="statIcon"></app-stat-icon>
            </td>
          </ng-container>

          <!-- Name Column -->
          <ng-container matColumnDef="melee">
            <th
              *matHeaderCellDef
              mat-header-cell
              mat-sort-header="Melee"
              matTooltip="The melee stat of this armor combination."
              >Melee
            </th>
            <td *matCellDef="let element" class="statColumn" mat-cell
              >{{ element.stats[ArmorStat.StatMelee] }}
              <app-stat-icon [stat]="ArmorStat.StatMelee" class="statIcon"></app-stat-icon>
            </td>
          </ng-container>

          <!-- Name Column -->
          <ng-container matColumnDef="mods">
            <th
              *matHeaderCellDef
              mat-header-cell
              mat-sort-header="Mods"
              matTooltip="The amount of mods required for each combination. Sorting after this takes the mod cost into account.">
              Used Mods
            </th>
            <td *matCellDef="let element" mat-cell>
              <app-table-mod-display
                [mods]="element.mods"
                [artifice]="element.artifice"
                class="modPreview"></app-table-mod-display>
            </td>
          </ng-container>

          <!-- Name Column -->
          <ng-container matColumnDef="exotic">
            <th *matHeaderCellDef mat-header-cell style="text-align: center">Exotic</th>
            <td *matCellDef="let element" mat-cell>
              <div class="itemDiv">
                <img
                  *ngIf="element.exotic !== undefined"
                  #tooltip="matTooltip"
                  class="itemIcon"
                  matTooltip="{{ element.exotic.name }}"
                  src="https://bungie.net/{{ element.exotic.icon }}" />
                <img
                  *ngIf="element.exotic !== undefined"
                  class="itemIconWatermark"
                  src="https://bungie.net/{{ element.exotic.watermark }}" />
                <img
                  *ngIf="element.exotic === undefined"
                  class="itemIcon"
                  src="https://www.bungie.net/common/destiny2_content/icons/b4d05ef69d0c3227a7d4f7f35bbc2848.png" />
              </div>
            </td>
          </ng-container>

          <!-- Vendor/Collection Column -->
          <ng-container matColumnDef="source">
            <th *matHeaderCellDef mat-header-cell>Sources</th>
            <td *matCellDef="let element" mat-cell>
              <span class="source-column">
                <img
                  *ngIf="!!element.usesCollectionRoll"
                  matTooltip="This build uses a collection exotic. You have to collect it before you can use it."
                  class="collectionIcon"
                  src="https://www.bungie.net/common/destiny2_content/icons/1d01287dd47af97fef16fa6acbac23ba.png" />
                <img
                  *ngIf="!!element.usesVendorRoll"
                  matTooltip="This build uses a vendor item. You have to collect it before you can use it."
                  class="vendorIcon"
                  src="https://www.bungie.net/common/destiny2_content/icons/8ef4b5bd32277dba9aee7c368404ad5d.jpg" />
              </span>
            </td>
          </ng-container>

          <!-- Name Column -->
          <ng-container matColumnDef="dropdown">
            <th *matHeaderCellDef mat-header-cell></th>
            <td *matCellDef="let element" mat-cell>
              <mat-icon
                *ngIf="expandedElement !== element"
                matTooltip="Click to show details for this build.">
                expand_more
              </mat-icon>
              <mat-icon
                *ngIf="expandedElement === element"
                matTooltip="Click to hide details for this build.">
                expand_less
              </mat-icon>
            </td>
          </ng-container>

          <!-- Total Column -->
          <ng-container matColumnDef="total">
            <th
              *matHeaderCellDef
              mat-header-cell
              mat-sort-header="Total"
              matTooltip="The total sum of all stats in this armor combination."
              >Total
            </th>
            <td *matCellDef="let element" mat-cell>
              {{ getTotalStats(element) }}
            </td>
          </ng-container>

          <!-- Expanded Content Column - The detail row is made up of this one column that spans across all columns -->
          <ng-container matColumnDef="expandedDetail">
            <td *matCellDef="let element" [attr.colspan]="shownColumns.length" mat-cell>
              <div
                [@detailExpand]="element === expandedElement ? 'expanded' : 'collapsed'"
                class="result-element-detail">
                <app-expanded-result-content [element]="element"></app-expanded-result-content>
              </div>
            </td>
          </ng-container>

          <tr *matHeaderRowDef="shownColumns" mat-header-row></tr>
          <tr
            (click)="expandedElement = expandedElement === element ? null : element"
            *matRowDef="let element; columns: shownColumns"
            [class.example-expanded-row]="expandedElement === element"
            class="result-element-row"
            mat-row>
          </tr>
          <tr
            *matRowDef="let row; columns: ['expandedDetail']"
            class="result-detail-row"
            mat-row></tr>
        </table>
      </div>
      <mat-paginator
        [style.display]="viewMode === 'table' ? 'block' : 'none'"
        [pageSizeOptions]="[25, 50, 75, 100]"
        aria-label="Select page of periodic elements"
        pageSize="25"
        showFirstLastButtons>
      </mat-paginator>
    </div>
  </mat-card-content>
  <mat-card-actions *ngIf="totalResults > 0">
    <span class="flex-spacer"></span>

    <button mat-raised-button color="primary" (click)="saveBuilds()"
      >Download results as JSON</button
    >
  </mat-card-actions>
</mat-card>
